import { type ReactElement } from 'react';
import {
  useFormContext,
  useFormState,
  type FieldError,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';
import { FormInput, type FormInputProps } from 'components/ui/form/Input';

export type RHFFormInputProps<T extends FieldValues> = Omit<
  FormInputProps,
  'registration' | 'error' | 'id'
> & {
  name: FieldPath<T>;
  /** Defaults to `String(name)` so `Field.Label` `htmlFor` matches the control. */
  id?: string;
  /** When set, overrides the error read from form state (e.g. Storybook). */
  error?: FieldError;
};

/**
 * `FormInput` wired to RHF: `register(name)` + `errors[name]`.
 *
 * With `clearable`, the underlying `Input` clears via the DOM and fires `register`’s
 * `onChange`, so no `watch` / `setValue` / controlled `value` is required.
 *
 * Must be used inside a tree that provides `FormProvider` (e.g. {@link Form}).
 */
export const RHFFormInput = <T extends FieldValues>({
  name,
  id: idProp,
  label,
  error: errorProp,
  clearable = false,
  ...rest
}: RHFFormInputProps<T>): ReactElement => {
  const { register, control } = useFormContext<T>();
  const { errors } = useFormState({ control, name: [name] });
  const formError = errors[name] as FieldError | undefined;
  const error = errorProp ?? formError;
  const id = idProp ?? String(name);

  return (
    <FormInput
      {...rest}
      id={id}
      label={label}
      registration={register(name)}
      error={error}
      clearable={clearable}
    />
  );
};
